
import Button from '../../../components/base/Button';

interface PricingFeature {
  text: string;
  included: boolean;
}

interface PricingCardProps {
  name: string;
  price: string;
  period: string;
  description: string;
  features: PricingFeature[];
  isPopular?: boolean;
  ctaText: string;
  badge?: string;
}

export default function PricingCard({
  name,
  price,
  period,
  description,
  features,
  isPopular = false,
  ctaText,
  badge
}: PricingCardProps) {
  return (
    <div
      className={`relative rounded-2xl p-6 sm:p-8 transition-all duration-300 ${
        isPopular
          ? 'bg-gradient-to-br from-[#22577A] to-[#38a3a5] text-white shadow-2xl md:scale-105 border-4 border-[#66b3ff]'
          : 'bg-white border-2 border-gray-200 hover:border-[#66b3ff] hover:shadow-xl'
      }`}
    >
      {badge && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="bg-gradient-to-r from-[#66b3ff] to-[#b3d9ff] text-[#22577A] px-6 py-2 rounded-full text-sm font-bold shadow-lg">
            <i className="ri-star-fill mr-1"></i>
            {badge}
          </div>
        </div>
      )}

      <div className="mb-6">
        <h3
          className={`text-2xl font-bold mb-2 ${
            isPopular ? 'text-white' : 'text-[#22577A]'
          }`}
        >
          {name}
        </h3>
        <p
          className={`text-sm ${
            isPopular ? 'text-white/80' : 'text-gray-600'
          }`}
        >
          {description}
        </p>
      </div>

      <div className="mb-8">
        <div className="flex items-baseline gap-2">
          <span
            className={`text-3xl sm:text-4xl md:text-5xl font-bold ${
              isPopular ? 'text-white' : 'text-[#22577A]'
            }`}
          >
            {price}
          </span>
          {price !== 'Custom' && (
            <span
              className={`text-lg ${
                isPopular ? 'text-white/70' : 'text-gray-600'
              }`}
            >
              /{period}
            </span>
          )}
        </div>
      </div>

      <Button
        variant={isPopular ? 'secondary' : 'outline'}
        size="lg"
        fullWidth
        className={isPopular ? 'bg-white text-[#22577A] hover:bg-gray-100' : ''}
      >
        {ctaText}
        <i className="ri-arrow-right-line"></i>
      </Button>

      <div className="mt-8 pt-8 border-t border-gray-200/20">
        <p
          className={`text-sm font-semibold mb-4 ${
            isPopular ? 'text-white' : 'text-[#22577A]'
          }`}
        >
          What's included:
        </p>
        <ul className="space-y-3">
          {features.map((feature, index) => (
            <li
              key={index}
              className={`flex items-start gap-3 ${
                feature.included
                  ? isPopular
                    ? 'text-white'
                    : 'text-gray-700'
                  : 'text-gray-400'
              }`}
            >
              <i
                className={`${
                  feature.included
                    ? 'ri-check-line text-[#b3d9ff]'
                    : 'ri-close-line text-gray-400'
                } text-xl flex-shrink-0 mt-0.5`}
              ></i>
              <span className="text-sm">{feature.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
